import * as React from 'react';
import { cloneDeep } from 'lodash';
import { DeleteOutlined } from '@ant-design/icons';
import { message } from 'antd';
import { Attribute, ParameterConfig } from '@interface/index';
import { ExprMode, ReferenceTextArea } from '@component/index';
import '@style/component/response.less';
import { localizedString } from '@locale/index';
import { isEqual, isStructAgent } from '@lib/index';

export interface TextResponsePropsInfo {
  data: string[];
  parameters?: ParameterConfig[];
  update?: (next: string[]) => void;
  placeholder?: string;
  placeholderFrist?: string;
  className?: string;
  borderTop?: boolean;
  disableChange?: boolean;
  noReference?: boolean;
  parameterMap: { [key: string]: ParameterConfig };
  isSingleLine?: boolean;
  allowClearAll?: Boolean;
  rawCodeMode?: boolean;
  rawCodeType?: any;
  rawCodeMethodName?: string;
  rawCodeExprMode?: ExprMode;
  disableReferenceMode?: boolean;
  languageDepandent?: boolean;
  forList?: boolean;
  rich?: boolean;
}

export interface TextResponseStateInfo {
  newText: string;
  data: string[];
}

export class TextResponse extends React.Component<
  TextResponsePropsInfo,
  TextResponseStateInfo
> {
  $wrap?: HTMLDivElement;
  $list?: HTMLUListElement;

  constructor(props: TextResponsePropsInfo) {
    super(props);
    this.state = { newText: '', data: [] };
  }

  onInit = () => {
    const props = this.props;
    const state = this.state;

    const nextData = cloneDeep(props.data || []).filter(Boolean);
    this.setState({ data: nextData || [] });
  }

  componentDidMount() {
    this.onInit();
  }

  componentDidUpdate(prevProps: TextResponsePropsInfo) {
    if (!isEqual(prevProps.data, this.props.data)) {
      this.onInit();
    }
  }

  render() {
    const {
      parameters = [],
      placeholder,
      placeholderFrist,
      className = '',
      borderTop = false,
      disableChange = false,
      noReference = false,
      parameterMap,
      isSingleLine = false,
      allowClearAll = false,
      rawCodeMode,
      disableReferenceMode,
      languageDepandent,
      forList,
      rich
    } = this.props;

    const { data, newText } = this.state;
    return (
      <div
        ref={(wrap: HTMLDivElement) => {
          this.$wrap = wrap;
        }}
        className={`text-response__wrap  ${className}`}
      >
        <ul
          ref={(list: HTMLUListElement) => {
            this.$list = list;
          }}
          className={`response__list text-response__list ${disableChange ? 'disabled' : ''}`}
          style={{
            borderTop: borderTop ? 'solid 1px rgba(0, 0, 0, 0.12)' : '',
            borderRadius: borderTop ? '4px' : ''
          }}
        >{((!isSingleLine || !data.length) && !disableChange) &&
          <li key={'text-response-new'}>
            {/* <span>{index + 1}</span> */}
            {<ReferenceTextArea
              autoSize
              resetOnEnter
              forList={forList}
              rich={rich}
              rows={rich ? 30 : 1}
              key="new-text"
              disableReferenceMode={disableReferenceMode}
              rawCodeMode={rawCodeMode}
              rawCodeType={this.props.rawCodeType}
              rawCodeExprMode={this.props.rawCodeExprMode}
              rawCodeMethodName={this.props.rawCodeMethodName}
              parameterMap={parameterMap}
              // rows={ }
              initParameters={parameters.map(
                (pm: ParameterConfig) => ({
                  id: pm.id,
                  name: pm.name,
                  type: pm.type,
                  promptQuestions: pm.promptQuestions,
                  proto: pm.proto,
                  attributes: pm.attributes,
                  parentId: pm.parentId,
                  multiValue: pm.multiValue
                })
              )}
              placeholder={(languageDepandent && isStructAgent())
                ? localizedString('language_dependency_hint') :
                ((
                  placeholderFrist || placeholder
                ) || (
                    noReference
                      ? localizedString('text_response_placeholder')
                      : `Enter text`
                  ))
              }
              disabled={disableChange}
              className={`text-response__textarea`}
              data={newText}
              update={(next: string) => {

                if (!next.length) {
                  return;
                }

                this.handleBlur(next, -1);
              }}
              onEnter={(value: string) => {
                this.handleEnter();
              }}
            />
            }
          </li>
          }
          {data.map(
            (response: string, index: number) => {

              return (
                <li key={`text-response-${index}`}>
                  {
                    <ReferenceTextArea
                      allowNewLine
                      autoSize
                      rich={this.props.rich}
                      rows={rich ? 30 : 1}
                      forList={forList}
                      rawCodeMode={rawCodeMode}
                      rawCodeType={this.props.rawCodeType}
                      rawCodeExprMode={this.props.rawCodeExprMode}
                      rawCodeMethodName={this.props.rawCodeMethodName}
                      disableReferenceMode={disableReferenceMode}
                      parameterMap={parameterMap}
                      initParameters={parameters.map(
                        (pm: ParameterConfig) => ({
                          id: pm.id,
                          name: pm.name,
                          type: pm.type,
                          promptQuestions: pm.promptQuestions,
                          proto: pm.proto,
                          attributes: pm.attributes,
                          parentId: pm.parentId,
                          multiValue: pm.multiValue
                        })
                      )}
                      placeholder={
                        (
                          index === 0
                            ? (placeholderFrist || placeholder)
                            : placeholder
                        ) || (
                          noReference
                            ? localizedString('text_response_placeholder')
                            : `Enter text`
                        )
                      }
                      disabled={disableChange}
                      className={`text-response__textarea ${className}`}
                      data={response}
                      update={(next: string) => {
                        this.handleBlur(next, index);
                      }}
                      onEnter={(value: string) => {
                        this.handleEnter();
                      }}
                    />
                  }
                  {disableChange ||
                    isSingleLine
                    ? ('') : (
                      <DeleteOutlined
                        style={{
                          color: 'rgba(0, 0, 0, 0.54)'
                        }}
                        onClick={() => {
                          const nextData = cloneDeep(data);
                          nextData.splice(index, 1);

                          this.setState({ data: nextData });
                          this.$update(nextData);
                        }} />
                    )}
                </li>
              );
            }
          )}
        </ul>
      </div>
    );
  }

  handleEnter = () => {
    setTimeout(() => {

      const doms: any = this.$list && this.$list.querySelectorAll(
        '.text-response__textarea textarea'
      );

      if (doms[0]) {
        doms[0].focus();
      }
    }, 500);
  };

  handleBlur = (value: string, index: number) => {
    const { data } = this.state;

    const target = cloneDeep(data);

    const responseMap: { [key: string]: number } = {};

    if (index === -1) {
      target.push(value);
    } else {
      target[index] = value;
    }

    target.forEach((str: string) => {
      if (!responseMap[str]) {
        responseMap[str] = 1;
      } else {
        responseMap[str] += 1;
      }
    });

    if (responseMap[value] > 1) {
      message.warning(localizedString('text_response_repeated'));
      return;
    }

    this.setState({ data: target, newText: '' }, () => {
      this.$update(target);
    });
  };

  $update = (next: string[]) => {
    const { update } = this.props;

    const originData = (this.props.data || []).filter(Boolean);
    const nextData = cloneDeep(next.filter(Boolean));

    if (!isEqual(nextData, originData) && update) {
      update(nextData);
    }
  };
}
